/**
 * Billing page — plans catalog, current subscription status, promo activation,
 * recent invoices, referral entry.
 *
 * Route: /app/billing
 */

import { motion } from "framer-motion";
import {
  ArrowLeft,
  CalendarClock,
  Check,
  Crown,
  Gift,
  History,
  Shield,
  Sparkles,
  Users,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button, Card, Input, Sheet } from "@/design";
import {
  useActivatePromo,
  useCreateInvoice,
  useInvoices,
  usePlans,
  useSubscription,
  type Invoice,
  type Plan,
} from "@/api/billing";
import { PaymentSheet } from "@/components/billing/PaymentSheet";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";

export function BillingPage() {
  const navigate = useNavigate();
  const { data: plans, isLoading } = usePlans();
  const { data: sub } = useSubscription();
  const { data: invoices } = useInvoices();

  const createInvoice = useCreateInvoice();
  const [activeInvoice, setActiveInvoice] = useState<Invoice | null>(null);
  const [activePlan, setActivePlan] = useState<Plan | null>(null);
  const [promoOpen, setPromoOpen] = useState(false);

  const onChoose = async (plan: Plan) => {
    haptic.impact("medium");
    try {
      const invoice = await createInvoice.mutateAsync({ plan_id: plan.id });
      setActivePlan(plan);
      setActiveInvoice(invoice);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось создать счёт");
    }
  };

  return (
    <div className="px-5 pt-4 pb-safe-nav space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={() => {
            haptic.select();
            navigate(-1);
          }}
          className="size-10 rounded-full glass grid place-items-center hover:bg-white/[0.06] transition-colors"
          aria-label="Назад"
        >
          <ArrowLeft className="size-4" />
        </button>
        <div className="text-[15px] font-semibold tracking-tight">Подписка</div>
        <button
          onClick={() => {
            haptic.select();
            navigate("/app/referrals");
          }}
          className="size-10 rounded-full glass grid place-items-center hover:bg-white/[0.06] transition-colors"
          aria-label="Рефералы"
        >
          <Gift className="size-4" />
        </button>
      </div>

      {/* Current subscription hero */}
      <SubscriptionHero sub={sub} />

      {/* Plans */}
      <div className="space-y-2">
        <div className="text-xs text-text-muted uppercase tracking-wider font-medium px-1">
          Тарифы
        </div>
        {isLoading ? (
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-10 text-center text-xs text-text-muted">
            Загрузка тарифов…
          </div>
        ) : (
          <div className="space-y-3">
            {(plans ?? []).map((p, i) => (
              <PlanCard
                key={p.id}
                plan={p}
                isCurrent={sub?.plan_id === p.id && sub?.is_active}
                delay={i * 0.04}
                onChoose={onChoose}
                loading={createInvoice.isPending && activePlan?.id === p.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Promo + invoices */}
      <Card padded={false} className="overflow-hidden">
        <button
          onClick={() => {
            haptic.select();
            setPromoOpen(true);
          }}
          className="w-full flex items-center gap-3 p-4 hover:bg-white/[0.03] transition-colors"
        >
          <div className="size-10 rounded-xl bg-accent-soft text-accent grid place-items-center">
            <Sparkles className="size-4" />
          </div>
          <div className="flex-1 text-left">
            <div className="text-[14px] font-medium">Промокод</div>
            <div className="text-xs text-text-muted">Активировать бесплатный план</div>
          </div>
        </button>
        <div className="h-px bg-white/[0.04]" />
        <button
          onClick={() => {
            haptic.select();
            navigate("/app/referrals");
          }}
          className="w-full flex items-center gap-3 p-4 hover:bg-white/[0.03] transition-colors"
        >
          <div className="size-10 rounded-xl bg-accent-soft text-accent grid place-items-center">
            <Users className="size-4" />
          </div>
          <div className="flex-1 text-left">
            <div className="text-[14px] font-medium">Реферальная программа</div>
            <div className="text-xs text-text-muted">Зарабатывайте с приглашений</div>
          </div>
        </button>
      </Card>

      {/* Recent invoices */}
      {invoices && invoices.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-text-muted uppercase tracking-wider font-medium px-1 flex items-center gap-1.5">
            <History className="size-3" />
            История счетов
          </div>
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04] overflow-hidden">
            {invoices.slice(0, 10).map((inv) => (
              <InvoiceRow key={inv.id} invoice={inv} />
            ))}
          </div>
        </div>
      )}

      <PaymentSheet
        open={!!activeInvoice}
        onClose={() => setActiveInvoice(null)}
        invoice={activeInvoice}
        plan={activePlan}
      />

      <PromoSheet open={promoOpen} onClose={() => setPromoOpen(false)} />

      {/* Disclaimer — мелкая пометка про ответственность за аккаунты */}
      <div className="pt-2 px-1 text-[10.5px] text-text-faint leading-relaxed text-center">
        Сервис не гарантирует срок жизни Telegram-аккаунтов: он зависит от
        истории IP, даты регистрации и&nbsp;политики Telegram. Блокировка
        аккаунта не является основанием для возврата средств за подписку.
        Подробнее — в&nbsp;«Условиях использования» в&nbsp;Настройках.
      </div>
    </div>
  );
}

// ============================================================
// Sub-components
// ============================================================

function SubscriptionHero({ sub }: { sub: ReturnType<typeof useSubscription>["data"] }) {
  const active = sub?.is_active;
  const days = sub?.days_remaining ?? 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
    >
      <Card padded={false} className="relative overflow-hidden">
        <div
          className={cn(
            "absolute inset-0 pointer-events-none",
            active
              ? "bg-gradient-to-br from-accent/25 via-transparent to-transparent"
              : "bg-gradient-to-br from-white/[0.04] via-transparent to-transparent",
          )}
          aria-hidden
        />
        <div className="relative p-6 space-y-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-medium">
            <span
              className={cn(
                "size-1.5 rounded-full",
                active ? "bg-success animate-pulse" : "bg-text-dim",
              )}
            />
            <span className={active ? "text-success" : "text-text-muted"}>
              {active ? "Подписка активна" : "Подписка не активна"}
            </span>
          </div>

          <div>
            <div className="text-[10px] text-text-dim uppercase tracking-wider font-medium">
              Текущий план
            </div>
            <div className="text-[28px] leading-tight font-semibold tracking-tight mt-0.5">
              {sub?.plan_title ?? (
                <span className="text-text-muted">Нет активного плана</span>
              )}
            </div>
          </div>

          {active && (
            <div className="flex items-center gap-3 pt-1">
              <div className="flex items-center gap-2 text-[12px] text-text-muted">
                <CalendarClock className="size-3.5" />
                Осталось:{" "}
                <span className="text-text font-medium tabular-nums">
                  {days} {pluralDays(days)}
                </span>
              </div>
            </div>
          )}

          {active && sub && (
            <div className="grid grid-cols-2 gap-2 pt-1">
              <Pill icon={<Users className="size-3" />} label="Аккаунты" value={sub.max_accounts} />
              <Pill icon={<Shield className="size-3" />} label="Чаты" value={sub.max_chats} />
            </div>
          )}
        </div>
      </Card>
    </motion.div>
  );
}

function Pill({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2 flex items-center gap-2">
      <span className="text-text-dim">{icon}</span>
      <div className="text-[10px] text-text-dim uppercase tracking-wider">{label}</div>
      <div className="ml-auto text-[13px] font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function PlanCard({
  plan,
  isCurrent,
  delay,
  onChoose,
  loading,
}: {
  plan: Plan;
  isCurrent: boolean | undefined;
  delay: number;
  onChoose: (p: Plan) => void;
  loading: boolean;
}) {
  const popular = plan.badge === "Популярно";
  const top = plan.badge === "Топ";
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        "relative rounded-3xl border overflow-hidden",
        popular || top
          ? "border-accent/30 bg-gradient-to-br from-accent/[0.08] via-white/[0.02] to-transparent"
          : "border-white/[0.06] bg-white/[0.02]",
      )}
    >
      {plan.badge && (
        <div className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full bg-gradient-accent text-white shadow-[0_4px_16px_-4px_rgba(255,69,0,0.6)]">
          {top && <Crown className="size-3" />}
          {plan.badge}
        </div>
      )}

      <div className="p-5 space-y-4">
        <div>
          <div className="text-[10px] text-text-dim uppercase tracking-wider font-medium">
            {plan.tagline}
          </div>
          <div className="text-xl font-semibold tracking-tight mt-0.5">{plan.title}</div>
          <div className="flex items-baseline gap-1 mt-2">
            <span className="text-[36px] leading-none font-semibold tabular-nums tracking-tight">
              ${plan.price_usd.toFixed(0)}
            </span>
            <span className="text-text-muted text-sm">/ {plan.duration_days} дн.</span>
          </div>
        </div>

        <ul className="space-y-1.5">
          {plan.perks.map((perk) => (
            <li key={perk} className="flex items-center gap-2 text-[13px] text-text-muted">
              <span
                className={cn(
                  "size-4 rounded-full grid place-items-center shrink-0",
                  popular || top ? "bg-accent/20 text-accent" : "bg-white/[0.06] text-text-dim",
                )}
              >
                <Check className="size-2.5" />
              </span>
              {perk}
            </li>
          ))}
        </ul>

        <Button
          fullWidth
          variant={isCurrent ? "secondary" : "primary"}
          onClick={() => !isCurrent && onChoose(plan)}
          loading={loading}
          disabled={isCurrent}
        >
          {isCurrent ? "Текущий план" : `Оплатить $${plan.price_usd.toFixed(0)}`}
        </Button>
      </div>
    </motion.div>
  );
}

function InvoiceRow({ invoice }: { invoice: Invoice }) {
  const tone =
    invoice.status === "paid"
      ? "text-success"
      : invoice.status === "pending"
      ? "text-text-muted"
      : "text-danger";
  return (
    <div className="px-4 py-3 flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[13px] font-medium truncate">{invoice.plan_title}</div>
          <div className="text-[13px] font-semibold tabular-nums shrink-0">
            ${invoice.amount_usd.toFixed(2)}
          </div>
        </div>
        <div className={cn("text-[11px] mt-0.5 capitalize", tone)}>
          {invoice.status === "paid"
            ? `Оплачено ${formatDate(invoice.paid_at ?? invoice.created_at)}`
            : invoice.status === "pending"
            ? `Ожидает оплаты · ${formatDate(invoice.created_at)}`
            : invoice.status === "expired"
            ? "Истёк срок"
            : "Отменён"}
        </div>
      </div>
    </div>
  );
}

function PromoSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [code, setCode] = useState("");
  const mut = useActivatePromo();

  const submit = async () => {
    if (!code.trim()) return;
    try {
      const res = await mut.mutateAsync({ code: code.trim() });
      toast.success(`Активирован план: ${res.plan_title}`);
      haptic.notify("success");
      setCode("");
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось активировать промокод");
      haptic.notify("error");
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Промокод"
      description="Введите код, чтобы активировать план без оплаты"
    >
      <div className="space-y-4">
        <Input
          placeholder="ВВЕДИ-КОД"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          autoFocus
        />
        <Button
          fullWidth
          onClick={submit}
          loading={mut.isPending}
          disabled={!code.trim()}
        >
          Активировать
        </Button>
      </div>
    </Sheet>
  );
}

// ============================================================
// Helpers
// ============================================================

function pluralDays(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "день";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "дня";
  return "дней";
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
}
